import { useSelector } from 'react-redux';
import { Url } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { isUserAdminLike } from '../../redux/modules/groups/helpers';
import { groupSelector } from '../../redux/modules/user/selectors';
import { useUnpaginatedUsers } from './useUnpaginatedUsers';

// One request for the whole app: all active admin users, mapped to a URL set in
// the query cache. Every caller shares the same query key, so React Query fetches
// this only once and reuses it across every queue tile.
const useAdminUserUrls = (): Set<Url> | undefined => {
  const groups = useSelector(groupSelector);
  const adminGroupIds = groups
    .filter(g => isUserAdminLike(g.name))
    .map(g => g.id);

  const { data } = useUnpaginatedUsers(
    { groups: adminGroupIds, deleted: false },
    { select: users => new Set(users.map(u => u.url)) }
  );
  return data;
};

/**
 * Number of users with access to a queue, counting admins who have implicit
 * access to every queue even when they are not explicitly assigned.
 *
 * Computed as the union of "all org admins" and "users explicitly assigned to
 * the queue":
 *   queue.users.length + adminUrls.size − (queue.users that are admins)
 *
 * Only a single (shared) request is made for the admin list; the per-queue term
 * is a client-side set intersection over the already-loaded `queue.users` URLs.
 */
export const useQueueAssignedUsersCount = (queue: Queue): number => {
  const adminUrls = useAdminUserUrls();
  if (!adminUrls) {
    // Graceful fallback while the admin list is loading.
    return queue.users.length;
  }
  const assignedAdmins = queue.users.filter(url => adminUrls.has(url)).length;
  return queue.users.length + adminUrls.size - assignedAdmins;
};
